import { FormField, Switch } from './FormField.jsx';

// Renders one admin-defined signup field.
// NOTE: we deliberately do NOT set the HTML5 `required` attribute here —
// browser-native validation tooltips are short and ugly ("Required").
// Instead the required check runs in JS (see validateCustomFieldsClient
// below) and shows "<Label> is required" inline under the field.
export function DynamicField({ field, value, onChange, error }) {
  const common = {
    value: value ?? (field.type === 'checkbox' ? false : ''),
    placeholder: field.placeholder || undefined,
  };

  if (field.type === 'checkbox') {
    return (
      <div>
        <Switch
          checked={!!value}
          onChange={onChange}
          label={field.label + (field.required ? ' *' : '')}
          hint={field.helpText}
        />
        {error && <span className="block text-[11px] text-danger mt-1">{error}</span>}
      </div>
    );
  }

  const input = (() => {
    if (field.type === 'textarea') {
      return (
        <textarea
          {...common}
          className="input"
          rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          {...common}
          className="input"
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}…`}</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    const htmlType = field.type === 'tel' ? 'tel'
      : field.type === 'number' ? 'number'
      : field.type === 'date' ? 'date'
      : field.type === 'url' ? 'url'
      : field.type === 'email' ? 'email'
      : 'text';
    return (
      <input
        {...common}
        type={htmlType}
        className="input"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  })();

  return (
    <FormField label={field.label} required={field.required} hint={field.helpText} error={error}>
      {input}
    </FormField>
  );
}

// Client-side version of the server's validateCustomFields. Runs before the
// network round-trip so required errors show up immediately on Save/Submit.
// Returns { ok, values, errorsByKey }.
export function validateCustomFieldsClient(defs, values) {
  const errors = {};
  const out = {};
  for (const def of defs) {
    if (!def.active) continue;
    const raw = values?.[def.key];
    const empty = raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0);
    if (empty) {
      if (def.required) errors[def.key] = `${def.label} is required`;
      continue;
    }
    switch (def.type) {
      case 'email':
        if (!/^\S+@\S+\.\S+$/.test(raw)) errors[def.key] = `${def.label} must be a valid email`;
        else out[def.key] = String(raw).trim().toLowerCase();
        break;
      case 'url':
        try { new URL(raw); out[def.key] = raw; }
        catch { errors[def.key] = `${def.label} must be a URL`; }
        break;
      case 'number': {
        const n = Number(raw);
        if (!Number.isFinite(n)) errors[def.key] = `${def.label} must be a number`;
        else out[def.key] = n;
        break;
      }
      case 'select':
        if (!def.options.includes(raw)) errors[def.key] = `${def.label} has an invalid option`;
        else out[def.key] = raw;
        break;
      case 'checkbox':
        out[def.key] = !!raw;
        break;
      case 'date':
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(raw))) errors[def.key] = `${def.label} must be a date`;
        else out[def.key] = raw;
        break;
      default:
        out[def.key] = raw;
    }
  }
  return { ok: Object.keys(errors).length === 0, values: out, errorsByKey: errors };
}
